/**
 * Generate Jig stage-tools module.
 * Applies AI-authored stage tools to serialized Jig stage data, including labels,
 * transforms, material changes, alpha fades, and viewpoint updates.
 */
import type { StageInstructionWithTools, ToolData, ViewpointSceneContext } from './generate-jig.types'
import { applyViewpointToStage } from './viewpoint/apply-viewpoint-to-stage'

declare var consoleWarn: Function

export function axisAngleToQuaternion(axis: string, angleDegrees: number): { w: number; x: number; y: number; z: number } {
  const angleRadians = angleDegrees * (Math.PI / 180)
  const halfAngle = angleRadians / 2
  const sinHalf = Math.sin(halfAngle)
  const cosHalf = Math.cos(halfAngle)

  let axisX = 0
  let axisY = 0
  let axisZ = 0

  if (axis === 'X') {
    axisX = 1
  } else if (axis === 'Y') {
    axisY = 1
  } else if (axis === 'Z') {
    axisZ = 1
  }

  return {
    w: -cosHalf,
    x: -(axisX * sinHalf),
    y: axisY * sinHalf,
    z: axisZ * sinHalf,
  }
}

export function hexToRGBA(hex: string, alpha: number): string {
  const cleanHex = normalizeHexColor(hex)
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255

  return JSON.stringify({
    r: parseFloat(r.toFixed(6)),
    g: parseFloat(g.toFixed(6)),
    b: parseFloat(b.toFixed(6)),
    a: parseFloat(alpha.toFixed(6)),
  })
}

function normalizeHexColor(hex: string): string {
  const trimmedHex = String(hex || '').trim().replace(/^#/, '')

  if (/^[0-9a-fA-F]{3}$/.test(trimmedHex)) {
    return trimmedHex
      .split('')
      .map(function (char: string) { return char + char })
      .join('')
  }

  if (/^[0-9a-fA-F]{6}$/.test(trimmedHex)) {
    return trimmedHex
  }

  return 'FFFFFF'
}

/**
 * Resolves an AI-provided element name against serialized elements using exact,
 * object-name, uid-fragment, suffix, and partial-match fallbacks.
 */
export function findElementByName(elements: any[], elementName: string): any {
  if (!elements || !elementName) {
    return null
  }

  const searchName = elementName.toLowerCase()
  let element = elements.find(function (el: any) {
    const name = el && el.Name ? String(el.Name).toLowerCase() : ''
    return name === searchName
  })

  if (!element) {
    element = elements.find(function (el: any) {
      const objectName = el && el.ElementData && el.ElementData.ObjectName ? String(el.ElementData.ObjectName).toLowerCase() : ''
      return objectName === searchName
    })
  }

  if (!element && searchName.indexOf('_') >= 0) {
    const parts = searchName.split('_')
    const uidPart = parts.find(function (part: string) {
      return part.indexOf('-') >= 0
    })

    if (uidPart) {
      element = elements.find(function (el: any) {
        const objectName = el && el.ElementData && el.ElementData.ObjectName ? String(el.ElementData.ObjectName).toLowerCase() : ''
        return objectName.indexOf(uidPart) >= 0
      })
    }
  }

  if (!element) {
    const suffix = '_' + searchName
    const endsWithMatches = elements.filter(function (el: any) {
      const objectName = el && el.ElementData && el.ElementData.ObjectName ? String(el.ElementData.ObjectName).toLowerCase() : ''
      return objectName.length > suffix.length && objectName.indexOf(suffix, objectName.length - suffix.length) >= 0
    })
    if (endsWithMatches.length === 1) {
      element = endsWithMatches[0]
    } else if (endsWithMatches.length > 1) {
      consoleWarn('findElementByName: multiple suffix matches for "' + elementName + '", using first of ' + endsWithMatches.length)
      element = endsWithMatches[0]
    }
  }

  if (!element) {
    element = elements.find(function (el: any) {
      const objectName = el && el.ElementData && el.ElementData.ObjectName ? String(el.ElementData.ObjectName).toLowerCase() : ''
      const shortName = el && el.Name ? String(el.Name).toLowerCase() : ''

      return (
        objectName.indexOf(searchName) >= 0 ||
        shortName.indexOf(searchName) >= 0 ||
        searchName.indexOf(objectName) >= 0 ||
        searchName.indexOf(shortName) >= 0
      )
    })
  }

  return element
}

export function getMaterialItemsArray(element: any): any[] | null {
  if (!element || !element.ElementData || !element.ElementData.Material || !element.ElementData.Material.Properties) {
    return null
  }

  if (Array.isArray(element.ElementData.Material.Properties._items)) {
    return element.ElementData.Material.Properties._items
  }

  if (Array.isArray(element.ElementData.Material.Properties._Items)) {
    return element.ElementData.Material.Properties._Items
  }

  return null
}

/**
 * Applies every supported visual tool to a single stage, mutating the serialized
 * stage payload in place so it can be sent back to the Jig API.
 */
export function applyToolsToStage(stage: any, stageData: StageInstructionWithTools, sceneContext?: ViewpointSceneContext | null): void {
  if (!stage || !Array.isArray(stage.SerializedElements)) {
    return
  }

  const elements = stage.SerializedElements

  if (Array.isArray(stageData.labels)) {
    stageData.labels.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (element && element.ElementData) {
        element.ElementData.Label = String(tool.labelText || '')
        element.ElementData.LabelOnForThisStage = true
      }
    })
  }

  if (Array.isArray(stageData.colorHighlights)) {
    stageData.colorHighlights.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element) {
        return
      }

      const objectName = element && element.ElementData && element.ElementData.ObjectName ? String(element.ElementData.ObjectName).toLowerCase() : ''
      if (objectName.indexOf('innerparent') >= 0) {
        return
      }

      const materialItems = getMaterialItemsArray(element)
      if (!materialItems) {
        return
      }

      const emissionProperty = materialItems.find(function (item: any) {
        return item && item.Name === '_EmissionColor'
      })

      if (emissionProperty) {
        emissionProperty.SerializedValue = hexToRGBA(String(tool.color || '#FFFFFF'), 1.0)
      }
    })
  }

  if (Array.isArray(stageData.showcase)) {
    stageData.showcase.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }

      const target = findElementByName(elements, String(tool.elementName))
      if (!target) {
        return
      }

      const fadeAlpha = typeof tool.fadeAlpha === 'number' ? tool.fadeAlpha : 0.2

      elements.forEach(function (element: any) {
        const objectName = element && element.ElementData && element.ElementData.ObjectName ? String(element.ElementData.ObjectName).toLowerCase() : ''
        if (objectName.indexOf('innerparent') >= 0) {
          return
        }

        const materialItems = getMaterialItemsArray(element)
        if (!materialItems) {
          return
        }

        const colorProperty = materialItems.find(function (item: any) {
          return item && item.Name === '_Color'
        })

        if (!colorProperty || !colorProperty.SerializedValue) {
          return
        }

        try {
          const colorValue = JSON.parse(colorProperty.SerializedValue)
          colorValue.a = parseFloat((element === target ? 1 : fadeAlpha).toFixed(6))
          colorProperty.SerializedValue = JSON.stringify(colorValue)
        } catch (_error) {
          // keep existing value on invalid JSON
        }
      })
    })
  }

  if (Array.isArray(stageData.rotation)) {
    stageData.rotation.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element || !element.ElementData) {
        return
      }

      const axis = String(tool.axis || 'Y')
      const angle = typeof tool.angle === 'number' ? tool.angle : 0
      const quat = axisAngleToQuaternion(axis, angle)

      if (!element.ElementData.Rotation) {
        element.ElementData.Rotation = { x: 0, y: 0, z: 0, w: 1 }
      }

      element.ElementData.Rotation.w = quat.w
      element.ElementData.Rotation.x = quat.x
      element.ElementData.Rotation.y = quat.y
      element.ElementData.Rotation.z = quat.z
    })
  }

  if (Array.isArray(stageData.move)) {
    stageData.move.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element || !element.ElementData) {
        return
      }

      if (!element.ElementData.Position) {
        element.ElementData.Position = { x: 0, y: 0, z: 0 }
      }

      const dx = typeof tool.x === 'number' ? tool.x : 0
      const dy = typeof tool.y === 'number' ? tool.y : 0
      const dz = typeof tool.z === 'number' ? tool.z : 0
      element.ElementData.Position.x += dx
      element.ElementData.Position.y += dy
      element.ElementData.Position.z += dz
    })
  }

  if (Array.isArray(stageData.scale)) {
    stageData.scale.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element || !element.ElementData) {
        return
      }

      if (!element.ElementData.Scale) {
        element.ElementData.Scale = { x: 1, y: 1, z: 1 }
      }

      if (typeof tool.uniform === 'number') {
        element.ElementData.Scale.x = tool.uniform
        element.ElementData.Scale.y = tool.uniform
        element.ElementData.Scale.z = tool.uniform
        return
      }

      if (typeof tool.x === 'number') {
        element.ElementData.Scale.x = tool.x
      }
      if (typeof tool.y === 'number') {
        element.ElementData.Scale.y = tool.y
      }
      if (typeof tool.z === 'number') {
        element.ElementData.Scale.z = tool.z
      }
    })
  }

  if (Array.isArray(stageData.alpha)) {
    stageData.alpha.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element || !element.ElementData) {
        return
      }
      element.ElementData.Alpha = typeof tool.alpha === 'number' ? tool.alpha : 1
    })
  }

  if (Array.isArray(stageData.viewpoint)) {
    applyViewpointToStage(stage, stageData.viewpoint, sceneContext || null)
  }
}
